import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15;
import { set, computed, flow, observable, runInAction } from "mobx";
import { getJwtPayload, realCommerceRequest } from "../utils";
import { RealCommerceError } from "../exceptions";
import { Installation, PaddleSubscription, License, Invoice, RpmProductVariantToSubscription, Affiliate } from ".";
import { routeLocationAffiliateGet, routeLocationUserGet, routeLocationUserPatch, routeLocationUserSessionDelete, routeLocationInvoicesGet, routeLocationUserSessionPatch, routeLocationLicensesGet, routeLocationSubscriptionsGet } from "@devowl-wp/api-real-commerce";
import { ESubscriptionStatus } from "@devowl-wp/api-real-commerce";
let Session = (_class = class Session {
  get authorizationHeader() {
    return {
      Authorization: `Bearer ${this.jwt}`
    };
  }
  get canSignUpAsAffiliate() {
    var _this$affiliateError, _this$affiliateError$;
    return ((_this$affiliateError = this.affiliateError) === null || _this$affiliateError === void 0 ? void 0 : _this$affiliateError.status) === 401 &&
    // Pending and blocked also has 401 error code, so we need to check if it is an empty response
    ((_this$affiliateError$ = this.affiliateError.codes) === null || _this$affiliateError$ === void 0 ? void 0 : _this$affiliateError$.length) === 0 && !this.affiliate;
  }
  get jwtPayload() {
    return getJwtPayload(this.jwt);
  }
  get activeSubscriptions() {
    const res = Array.from(this.subscriptions.values()).filter(s => [ESubscriptionStatus.Active, ESubscriptionStatus.CancellationPending, ESubscriptionStatus.Canceled].indexOf(s.data.status) > -1);
    res.sort((a, b) => a.sortIndex - b.sortIndex);
    return res;
  }
  get subscriptionProductIds() {
    return Array.from(this.subscriptions.values()).map(_ref => {
      let {
        data: {
          productPlan: {
            product: {
              id
            }
          }
        }
      } = _ref;
      return id;
    });
  }
  constructor(_jwt, store) {
    _initializerDefineProperty(this, "user", _descriptor, this);
    _initializerDefineProperty(this, "affiliate", _descriptor2, this);
    _initializerDefineProperty(this, "affiliateError", _descriptor3, this);
    _initializerDefineProperty(this, "busy", _descriptor4, this);
    _initializerDefineProperty(this, "busyAffiliate", _descriptor5, this);
    _initializerDefineProperty(this, "busyLicenses", _descriptor6, this);
    _initializerDefineProperty(this, "busySubscriptions", _descriptor7, this);
    _initializerDefineProperty(this, "busyInvoices", _descriptor8, this);
    _initializerDefineProperty(this, "hasEnvatoPurchase", _descriptor9, this);
    _initializerDefineProperty(this, "licenses", _descriptor10, this);
    _initializerDefineProperty(this, "subscriptions", _descriptor11, this);
    _initializerDefineProperty(this, "installations", _descriptor12, this);
    _initializerDefineProperty(this, "rpmProductVariantToSubscription", _descriptor13, this);
    _initializerDefineProperty(this, "invoices", _descriptor14, this);
    _initializerDefineProperty(this, "jwt", _descriptor15, this);
    this.store = void 0;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchUser = flow(function* () {
      this.busy = true;
      try {
        const response = yield realCommerceRequest({
          location: routeLocationUserGet,
          settings: {
            headers: this.authorizationHeader
          }
        });
        this.user = response.user;
        try {
          yield this.fetchAffiliate();
        } catch (e) {
          // Error handling is done via affiliate status properties within this class
        }
      } catch (e) {
        console.log(e);
        throw new RealCommerceError(e);
      } finally {
        this.busy = false;
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchAffiliate = flow(function* () {
      this.busyAffiliate = true;
      try {
        const response = yield realCommerceRequest({
          location: routeLocationAffiliateGet,
          settings: {
            headers: this.authorizationHeader
          }
        });
        if (this.affiliate) {
          set(this.affiliate.data, response);
        } else {
          this.affiliate = new Affiliate(response, this);
        }
        this.affiliateError = undefined;
      } catch (e) {
        console.log(e);
        this.affiliateError = new RealCommerceError(e);
        throw this.affiliateError;
      } finally {
        this.busyAffiliate = false;
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchSubscriptionsAndLicenses = flow(function* () {
      // Do not fetch in concurrent, as this could lead to longer times as both requests are contacting the same db tables
      yield this.fetchSubscriptions();
      yield this.fetchLicenses();
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.updateDetails = flow(function* (user) {
      this.busy = true;
      yield this.probablyRenewJwt();
      try {
        const response = yield realCommerceRequest({
          location: routeLocationUserPatch,
          request: {
            user
          },
          settings: {
            headers: this.authorizationHeader
          }
        });
        this.user = response.user;
      } catch (e) {
        console.log(e);
        throw new RealCommerceError(e);
      } finally {
        this.busy = false;
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.logout = flow(function* () {
      try {
        yield realCommerceRequest({
          location: routeLocationUserSessionDelete,
          settings: {
            headers: this.authorizationHeader
          }
        });
      } catch (e) {
        throw new RealCommerceError(e);
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchInvoices = flow(function* () {
      this.busyInvoices = true;
      this.invoices = [];
      yield this.probablyRenewJwt();
      try {
        const response = yield realCommerceRequest({
          location: routeLocationInvoicesGet,
          settings: {
            headers: this.authorizationHeader
          }
        });
        for (const row of response.invoices) {
          this.invoices.push(new Invoice(row, this));
        }
        this.hasEnvatoPurchase = response.hasEnvatoPurchase;
      } catch (e) {
        console.log(e);
        throw new RealCommerceError(e);
      } finally {
        this.busyInvoices = false;
      }
    });
    /**
     * When the JWT gets expired soon, let's try to regenerate it, otherwise logout.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.probablyRenewJwt = flow(function* (force) {
      if (this.jwtPayload.expired || force) {
        try {
          const {
            jwt
          } = yield realCommerceRequest({
            location: routeLocationUserSessionPatch,
            settings: {
              headers: this.authorizationHeader
            }
          });
          this.jwt = jwt;
        } catch (e) {
          console.log(e);
          throw new RealCommerceError(e);
        }
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchLicenses = flow(function* () {
      this.busyLicenses = true;
      yield this.probablyRenewJwt();
      try {
        const {
          licenses,
          rpmProductVariants
        } = yield realCommerceRequest({
          location: routeLocationLicensesGet,
          settings: {
            headers: this.authorizationHeader
          }
        });
        for (const row of licenses) {
          const {
            id
          } = row;
          const existing = this.licenses.get(id);
          if (existing) {
            set(existing.data, row);
          } else {
            this.licenses.set(id, new License(row, this));
          }
        }

        // Remove stale licenses
        const activeIds = licenses.map(_ref2 => {
          let {
            id
          } = _ref2;
          return id;
        });
        Array.from(this.licenses.keys()).forEach(potentialStaleId => {
          if (activeIds.indexOf(potentialStaleId) === -1) {
            this.licenses.delete(potentialStaleId);
          }
        });
        this.rpmProductVariantToSubscription.clear();
        for (const {
          subscription: {
            id
          },
          productVariant
        } of rpmProductVariants) {
          const rpmProductVariantToSubscription = this.rpmProductVariantToSubscription.get(id) || [];
          rpmProductVariantToSubscription.push(new RpmProductVariantToSubscription(productVariant, this));
          this.rpmProductVariantToSubscription.set(id, rpmProductVariantToSubscription);
        }
      } catch (e) {
        console.log(e);
        throw new RealCommerceError(e);
      } finally {
        this.busyLicenses = false;
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchSubscriptions = flow(function* () {
      this.busySubscriptions = true;
      yield this.probablyRenewJwt();
      try {
        const {
          subscriptions,
          installations
        } = yield realCommerceRequest({
          location: routeLocationSubscriptionsGet,
          settings: {
            headers: this.authorizationHeader
          }
        });
        let i = 0;
        for (const row of subscriptions) {
          const {
            id
          } = row;
          const existing = this.subscriptions.get(id);
          if (existing) {
            set(existing.data, row);
          } else {
            // TODO: differ between ecommerce systems
            this.subscriptions.set(id, new PaddleSubscription(row, this));
          }
          this.subscriptions.get(id).sortIndex = i;
          i++;
        }

        // Remove stale subscriptions
        const activeIds = subscriptions.map(_ref3 => {
          let {
            id
          } = _ref3;
          return id;
        });
        Array.from(this.subscriptions.keys()).forEach(potentialStaleId => {
          if (activeIds.indexOf(potentialStaleId) === -1) {
            this.subscriptions.delete(potentialStaleId);
          }
        });
        this.installations.clear();
        for (const row of installations) {
          const installationsToSubscriptions = this.installations.get(row.subscriptionId) || [];
          installationsToSubscriptions.push(new Installation(row, this));
          this.installations.set(row.subscriptionId, installationsToSubscriptions);
        }
      } catch (e) {
        console.log(e);
        throw new RealCommerceError(e);
      } finally {
        this.busySubscriptions = false;
      }
    });
    this.store = store;
    runInAction(() => {
      this.jwt = _jwt;
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "user", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "affiliate", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "affiliateError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "busy", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "busyAffiliate", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "busyLicenses", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "busySubscriptions", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "busyInvoices", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "hasEnvatoPurchase", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "licenses", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "subscriptions", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "installations", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "rpmProductVariantToSubscription", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "invoices", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "jwt", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "authorizationHeader", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "authorizationHeader"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canSignUpAsAffiliate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canSignUpAsAffiliate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "jwtPayload", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "jwtPayload"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "activeSubscriptions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "activeSubscriptions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "subscriptionProductIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "subscriptionProductIds"), _class.prototype)), _class);
export { Session };